import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { hoursToMilliseconds, minutesToMilliseconds } from 'date-fns'
import i18n from 'i18next'
import { toast } from 'sonner'

const DEFAULT_RETRY_COUNT = 0

/**
 * Backend errors carry a stable code in `message` (e.g. `stop_reason_required`). Known codes are
 * translated via `apiErrors.<code>`; anything else is shown as-is (legacy English messages).
 */
export const toErrorMessage = (error: unknown) => {
  const errorObject = Array.isArray(error) ? error[0] : error
  const message: string | undefined = errorObject?.code ?? errorObject?.message
  if (message && i18n.exists(`apiErrors.${message}`)) return i18n.t(`apiErrors.${message}` as never)
  return errorObject?.message ?? i18n.t('errors.error_generic')
}

export const _createQueryClient = () => {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (_error, query) => {
        if (query.meta?.skipGlobalErrorToast === true) return
        const retryCount =
          typeof query.options.retry === 'number' ? query.options.retry : DEFAULT_RETRY_COUNT
        if (query.state.fetchFailureCount > retryCount) {
          toast.error(toErrorMessage(_error))
        }
      },
    }),
    // Mirror the query cache: surface every failed mutation as a toast so writes can never fail
    // silently. A mutation opts out when it handles its own error — either a hook-level `onError`
    // (detected here) or an explicit `meta: { skipGlobalErrorToast: true }` (for call sites that
    // show their own feedback, e.g. `toast.promise`). Per-call `onError` is NOT detectable, so
    // those sites must use the meta flag if they want to suppress this default toast.
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.options.onError || mutation.meta?.skipGlobalErrorToast === true) return
        toast.error(toErrorMessage(error))
      },
    }),
    defaultOptions: {
      queries: {
        gcTime: hoursToMilliseconds(24),
        refetchOnWindowFocus: false,
        retry: DEFAULT_RETRY_COUNT,
        staleTime: minutesToMilliseconds(1),
      },
    },
  })
}

export let queryClient: QueryClient

export const createQueryClient = () => {
  if (queryClient) return queryClient
  queryClient = _createQueryClient()

  return queryClient
}

/**
 * @description Invalidates multiple queries based on the base query key
 * generated in our API client. This is useful since we can't access that key directly and we get
 * TypeScript errors if we try to call the generated query key functions with incorrect parameters.
 * However if we just want to access the root of the query key the only way is to call the function with an empty object
 * and suppress all TypeScript errors.
 */
export const invalidateQueriesByRootKey = (
  queryClient: QueryClient,
  queryKeyFns: ((opts: any) => any[])[],
  fnArgs: Record<string, unknown> = {},
) => {
  for (const queryKeyFn of queryKeyFns) {
    const queryKey = queryKeyFn(fnArgs)
    queryClient.invalidateQueries({ queryKey, exact: false })
  }
}

/**
 * @description See description for invalideQueriesByRootKey.
 */
export const removeQueriesByRootKey = (
  queryClient: QueryClient,
  queryKeyFns: ((opts: any) => any[])[],
) => {
  for (const queryKeyFn of queryKeyFns) {
    const queryKey = queryKeyFn({})
    queryClient.removeQueries({ queryKey, exact: false })
  }
}
